import { useEffect } from "react";

function selectOrCreate(selector, tagName, attributes) {
  const existing = document.head.querySelector(selector);
  if (existing) return { element: existing, created: false };
  const element = document.createElement(tagName);
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  document.head.append(element);
  return { element, created: true };
}

export function usePageMetadata({ title, description, canonicalPath }) {
  useEffect(() => {
    if (!title) return undefined;
    const previousTitle = document.title;
    const managed = [
      ["meta[name='description']", "meta", { name: "description" }, description],
      ["meta[property='og:title']", "meta", { property: "og:title" }, title],
      ["meta[property='og:description']", "meta", { property: "og:description" }, description],
      ["meta[property='og:type']", "meta", { property: "og:type" }, "article"],
      ["meta[name='twitter:title']", "meta", { name: "twitter:title" }, title],
      ["meta[name='twitter:description']", "meta", { name: "twitter:description" }, description],
    ].map(([selector, tagName, attributes, content]) => {
      const entry = selectOrCreate(selector, tagName, attributes);
      const previous = entry.element.getAttribute("content");
      if (content) entry.element.setAttribute("content", content);
      return { ...entry, previous };
    });
    const canonical = selectOrCreate("link[rel='canonical']", "link", { rel: "canonical" });
    const previousCanonical = canonical.element.getAttribute("href");
    if (canonicalPath) {
      canonical.element.setAttribute(
        "href",
        new URL(canonicalPath, window.location.origin).toString(),
      );
    }
    const removedImages = [
      ...document.head.querySelectorAll(
        "meta[property='og:image'], meta[name='twitter:image']",
      ),
    ].map((element) => ({ element, parent: element.parentNode }));
    removedImages.forEach(({ element }) => element.remove());
    document.title = title;
    return () => {
      document.title = previousTitle;
      managed.forEach(({ element, created, previous }) => {
        if (created) element.remove();
        else if (previous == null) element.removeAttribute("content");
        else element.setAttribute("content", previous);
      });
      if (canonical.created) canonical.element.remove();
      else if (previousCanonical == null) canonical.element.removeAttribute("href");
      else canonical.element.setAttribute("href", previousCanonical);
      removedImages.forEach(({ element, parent }) => parent?.append(element));
    };
  }, [canonicalPath, description, title]);
}

